<template>
  <layout>
    <page-header :header="$t('settings.sections.widget')" />
    <div class="content">
      <referral-link-form />
      <b-row>
        <b-col>
          <b-card :title="$t('general.general')">
            <b-form-group
              id="input-group-2"
              :label="$t('general.language')"
              label-for="input-2"
            >
              <b-form-select
                v-model="locale"
                :options="options"
              ></b-form-select>
            </b-form-group>

            <b-form-group :label="$t('settings.widget.redirect')">
              <b-form-input v-model="redirect"></b-form-input>
            </b-form-group>

            <b-form-group :label="$t('settings.widget.payment_redirect')">
              <b-form-input v-model="paymentRedirect"></b-form-input>
            </b-form-group>

            <b-form-group v-if="isUrl">
              <b-form-checkbox v-model="blank">
                {{ $t('settings.widget.blank_page') }}
              </b-form-checkbox>
            </b-form-group>

            <b-form-group :label="$t('settings.widget.google_api_key_label')">
              <b-form-input v-model="googleApiKey"></b-form-input>
            </b-form-group>

            <b-form-checkbox v-model="showMap">{{
              $t('settings.widget.show_map')
            }}</b-form-checkbox>

            <b-form-checkbox v-model="showArrivalBooking">{{
              $t('settings.widget.show_arrival_booking')
            }}</b-form-checkbox>
            <b-form-checkbox
              v-model="lastBooks"
              checked
              unchecked-value="no"
              value="yes"
            >
              {{ $t('settings.widget.show_last_bookings') }}
            </b-form-checkbox>
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-card :title="$t('settings.widget.instructions')">
            <p>
              {{
                redirect
                  ? $t('settings.widget.add_script_both')
                  : $t('settings.widget.add_script')
              }}
            </p>
            <pre>{{ script }}<i v-clipboard="() => script" :title="$t('general.copy')" class="icon-copy font-size-lg float-right cursor-pointer" @click="showCopyAlert"></i></pre>
            <br />
            <p>
              {{
                redirect
                  ? $t('settings.widget.add_code_redirect')
                  : $t('settings.widget.add_code')
              }}
            </p>
            <pre>{{ code }}<i v-clipboard="() => code" :title="$t('general.copy')" class="icon-copy font-size-lg float-right cursor-pointer" @click="showCopyAlert"></i></pre>
            <br />
            <div v-if="redirect">
              <p>{{ $t('settings.widget.banner_code') }}</p>
              <pre>{{ codeBanner }}<i v-clipboard="() => codeBanner" :title="$t('general.copy')" class="icon-copy font-size-lg float-right cursor-pointer" @click="showCopyAlert"></i></pre>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </layout>
</template>

<script>
import Vue from 'vue'
import { mapState } from 'vuex'
import ClipBoard from 'v-clipboard'
import assign from 'lodash-es/assign'

const ReferralLinkForm = () => import('./ReferralLinkForm.vue')

Vue.use(ClipBoard)

export default {
  name: 'WidgetForm',

  components: {ReferralLinkForm},

  props: {
    defaults: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      blank: true,
      googleApiKey: '',
      hideRideTypes: [],
      lastBooks: 'yes',
      locale: 'en',
      paymentRedirect: '',
      privacyPolicy: '',
      redirect: '',
      showArrivalBooking: false,
      showMap: false,
      tab: '',
      termsAndConditions: '',
    }
  },

  computed: {
    ...mapState('languageSwitcher', ['locales']),

    options() {
      return this.locales.map(locale => ({
        text: `${locale.name} (${locale.native})`,
        value: locale.locale,
      }))
    },

    tabsOptions() {
      return [
        {
          text: this.$t('widget.airport_transfer'),
          value: '',
        },
        {
          text: this.$t('widget.city_taxi'),
          value: 'cityTaxi',
        },
        {
          text: this.$t('widget.delivery'),
          value: 'delivery',
        },
      ]
    },

    code() {
      const widget = document.createElement('at-widget-booking')
      widget.setAttribute('locale', this.locale || 'en')
      if (this.privacyPolicy)
        widget.setAttribute('privacy-policy', this.privacyPolicy)
      if (this.googleApiKey)
        widget.setAttribute('google-map-key', this.googleApiKey)
      if (this.termsAndConditions)
        widget.setAttribute('terms-and-conditions', this.termsAndConditions)
      if (this.showMap) widget.setAttribute('show-map', this.showMap)
      if (this.showArrivalBooking)
        widget.setAttribute('show-arrival-booking', this.showArrivalBooking)

      if (this.tab) widget.setAttribute('tab', this.tab)

      if (this.partnerId) {
        widget.setAttribute('partner-id', this.partnerId)
      }
      if (this.paymentRedirect) {
        widget.setAttribute('payment-redirect', this.paymentRedirect)
      }

      if (this.hideRideTypes.length > 0) {
        widget.setAttribute('hide-ride-types', this.hideRideTypes.join(','))
      }
      widget.setAttribute('last-books', this.lastBooks)

      return this.elementToString(widget)
    },

    script() {
      const scriptEl = document.createElement('script')
      scriptEl.setAttribute(
        'src',
        `${process.env.VUE_APP_WIDGET_LOCATION}/main.js`,
      )
      return this.elementToString(scriptEl) //.replace('<script', '<script async')
    },

    codeBanner() {
      const widget = document.createElement('at-widget-booking')
      widget.setAttribute('locale', this.locale || 'en')
      if (this.blank && this.isUrl) widget.setAttribute('blank', 'true')
      if (this.redirect) widget.setAttribute('redirect', this.redirect)
      return this.elementToString(widget)
    },

    isUrl() {
      const { redirect } = this
      return (
        redirect &&
        (redirect.startsWith('http://') ||
          redirect.startsWith('https://') ||
          redirect.startsWith('/'))
      )
    },

    partnerId() {
        return (this.$account) ? this.$account.partner && this.$account.id : null
    },
  },

  mounted() {
    assign(this, this.defaults)
  },

  methods: {
    elementToString(el) {
      const tmp = document.createElement('div')
      tmp.appendChild(el)
      return tmp.innerHTML
    },

    showCopyAlert() {
      this.$toast.success(this.$t('general.copied'))
    },
  },
}
</script>
